import type { ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { useRouter } from 'next/router'
import { prefersReducedMotion } from '@/lib/preferences'

type Phase = 'idle' | 'closing' | 'closed' | 'opening'

type TransitionContextValue = {
    navigate: (href: string) => void
}

const TransitionContext = createContext<TransitionContextValue>({
    navigate: () => undefined
})

export const usePageTransition = (): TransitionContextValue => useContext(TransitionContext)

const CLOSE_MS = 420
const OPEN_MS = 760

/**
 * Pages change by fading through black, like closing and reopening your eyes.
 * Links call navigate(); browser back and forward get a shorter blink.
 */
export const PageTransition = ({ children }: { children: ReactNode }): ReactNode => {
    const router = useRouter()
    const [phase, setPhase] = useState<Phase>('idle')
    const ownNavigation = useRef(false)
    const timers = useRef<number[]>([])

    const clearTimers = () => {
        timers.current.forEach(timer => window.clearTimeout(timer))
        timers.current = []
    }

    const open = useCallback(() => {
        clearTimers()
        setPhase('opening')
        timers.current.push(window.setTimeout(() => setPhase('idle'), OPEN_MS))
    }, [])

    const navigate = useCallback((href: string) => {
        if (href === router.asPath) {
            window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
            return
        }

        if (prefersReducedMotion()) {
            void router.push(href)
            return
        }

        clearTimers()
        ownNavigation.current = true
        setPhase('closing')
        timers.current.push(window.setTimeout(() => {
            setPhase('closed')
            void router.push(href).catch(() => {
                ownNavigation.current = false
                open()
            })
        }, CLOSE_MS))
    }, [router, open])

    useEffect(() => {
        const handleStart = () => {
            if (ownNavigation.current || prefersReducedMotion()) return
            clearTimers()
            setPhase('closed')
        }

        const handleDone = () => {
            ownNavigation.current = false
            if (prefersReducedMotion()) {
                setPhase('idle')
                return
            }
            open()
        }

        router.events.on('routeChangeStart', handleStart)
        router.events.on('routeChangeComplete', handleDone)
        router.events.on('routeChangeError', handleDone)
        router.events.on('hashChangeComplete', handleDone)

        return () => {
            router.events.off('routeChangeStart', handleStart)
            router.events.off('routeChangeComplete', handleDone)
            router.events.off('routeChangeError', handleDone)
            router.events.off('hashChangeComplete', handleDone)
            clearTimers()
        }
    }, [router, open])

    const value = useMemo(() => ({ navigate }), [navigate])
    const covered = phase === 'closing' || phase === 'closed'

    return (
        <TransitionContext.Provider value={value}>
            {children}

            <div
                aria-hidden
                className='pointer-events-none fixed inset-0 z-[85] bg-ink'
                style={{
                    opacity: covered ? 1 : 0,
                    transitionProperty: 'opacity',
                    transitionDuration: `${phase === 'closing' ? CLOSE_MS : phase === 'closed' ? 0 : OPEN_MS}ms`,
                    transitionTimingFunction: 'cubic-bezier(0.65, 0, 0.35, 1)',
                    visibility: phase === 'idle' ? 'hidden' : 'visible'
                }}
            />
        </TransitionContext.Provider>
    )
}
